import React from "react";
import style from "./ConfigForm.module.scss";
import { labelFor, labelsFor } from "./optionsFormat";
import { resolveVisibleWhen } from "./useConfigFormState";
import { resolveFieldForVariant, resolveFieldOptions } from "./configFormHelpers";
import { hasValue } from "../../../utils";

const cx = (...classNames) => classNames.filter(Boolean).join(" ");

const formatField = (field, source, extra) => {
  const rawValue = source[field.key];
  let value;

  if (typeof field.viewFormat === "function") {
    value = field.viewFormat(source);
  } else if (field.options) {
    const options = resolveFieldOptions(field, source, extra);
    value = Array.isArray(rawValue) ? labelsFor(options, rawValue) : labelFor(options, rawValue);
  } else {
    value = rawValue;
  }

  return hasValue(value) ? value : field.emptyValue ?? "-";
};

const renderViewRow = (field, source, extra) => {
  const value = formatField(field, source, extra);
  return (
    <p className={style.form_view_row} key={field.key}>
      <label>{field.viewLabel || field.label}:</label>
      <span>{Array.isArray(value) ? value.join(", ") : value}</span>
    </p>
  );
};

const buildRenderItems = (visibleFields) => {
  const items = [];
  let i = 0;

  while (i < visibleFields.length) {
    const field = visibleFields[i];

    if (field.visibleWhen) {
      const run = [field];
      let j = i + 1;
      while (j < visibleFields.length && visibleFields[j].visibleWhen === field.visibleWhen) {
        run.push(visibleFields[j]);
        j += 1;
      }
      items.push({ type: "revealed", key: field.key, fields: run });
      i = j;
      continue;
    }

    items.push({ type: "single", key: field.key, field });
    i += 1;
  }

  return items;
};

/**
 * ConfigFormView — view-mode renderer for the shared ConfigForm engine.
 */
export const ConfigFormView = ({ sections, column, variant, extra, data, classNames = {} }) => {
  const visibleSections = (sections || []).filter((section) => !column || section.column === column);
  const source = data || {};

  return (
    <>
      {visibleSections.map((section) => {
        const visibleFields = (section.fields || [])
          .map((field) => resolveFieldForVariant(field, variant))
          .filter((field) => field.viewable !== false && field.visible !== false && resolveVisibleWhen(field.visibleWhen, source));
        if (!visibleFields.length) return null;

        const renderItems = buildRenderItems(visibleFields);

        return (
          <section className={cx(style.form_section, classNames.section)} key={section.key}>
            <div className={style.form_section_header}>
              {section.number !== undefined && <span className={style.form_section_number}>{section.number}</span>}
              <span className={style.form_section_title}>{section.title}</span>
            </div>
            <div className={style.form_section_body}>
              {renderItems.map((item) =>
                item.type === "revealed" ? (
                  <div className={style.form_field_revealed} key={item.key}>
                    {item.fields.map((field) => renderViewRow(field, source, extra))}
                  </div>
                ) : (
                  renderViewRow(item.field, source, extra)
                )
              )}
            </div>
          </section>
        );
      })}
    </>
  );
};
